import { localized } from '@cue/translate';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { cueArrowRight, cueCheck, cueClose, cueQrCode, IconComponent } from '@cue/ui/icons';
import { ControllableObjectsComponent, WorkingHoursComponent } from '@cue/ui';
import { FileGalleryComponent } from '@cue/ui/file-gallery';
import { CustomUnsafeSanitizePipe } from '@cue/security';
import { ImageWidthPipe, LocalizedStringPipe } from '@cue/utilities';
import { translation } from '@cue/translations';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PoisComponent } from '@cue/poi';
import { NavigationDetailInfo, NavigationService, QrIdListPipe } from '@cue/navigation-services';
import { DatePipe } from '@angular/common';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { AssistPropertyBM, LocalizedString } from '@cue/api';
import { PoisCanBeShownInResourcePipe, UnitDetailComponent } from '@cue/navigation-widgets';

@Component({
  selector: 'assist-multiview-renew-anonymous-detail',
  imports: [
    IconComponent,
    WorkingHoursComponent,
    FileGalleryComponent,
    CustomUnsafeSanitizePipe,
    LocalizedStringPipe,
    TranslocoPipe,
    ImageWidthPipe,
    ControllableObjectsComponent,
    DatePipe,
    PoisCanBeShownInResourcePipe,
    PoisComponent,
    QrIdListPipe,
    UnitDetailComponent,
  ],
  templateUrl: './multiview-renew-anonymous-detail.component.html',
  styleUrl: './multiview-renew-anonymous-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiviewRenewAnonymousDetailComponent {
  ICONS = {
    cueArrowRight,
    cueCheck,
    cueClose,
    cueQrCode,
  };

  navigationSettings = inject(NAVIGATION_SETTINGS);
  navigationService = inject(NavigationService);
  translocoService = inject(TranslocoService);
  translation = translation;
  qrCodeId = input.required<string>();
  item = input<NavigationDetailInfo | null>(null);
  loading = input<boolean>();

  availabilityTextMap = {
    '0': translation.availability.free,
    '1': translation.availability.tentative,
    '2': translation.availability.reserved,
    '3': translation.availability.notReservableNow,
    '4': translation.availability.notReservableNow,
    '5': translation.availability.notReservableNow,
    '6': translation.availability.notReservableNow,
    '7': translation.availability.notReservableNow,
    '9': translation.availability.reserved,
    '8': translation.availability.freeClosed,
  };

  availability = computed(() => this.item()?.schedule?.availabilityView[0]);

  private getFiltersFromNavigationDetail(
    detail: NavigationDetailInfo
  ): { name: LocalizedString; value: any; dataTypeId: number; visible: boolean }[] {
    return detail.properties
      .map((property) => ({
        name: property.name,
        visible: property.visible,
        dataTypeId: property.propertyTypeId,
        value: this.getValueFromProperty(property),
      }))
      .filter((x) => x.value);
  }

  protected getValueFromProperty(property: AssistPropertyBM & { propertyTypeId: number; valueChoices: any[] }) {
    switch (property.propertyTypeId) {
      case 1: {
        return {
          value: property.valueBoolean,
          imageUrl: property.file,
        };
      }
      case 2: {
        return {
          value: property.valueNumber,
          imageUrl: property.file,
        };
      }
      case 3: {
        return {
          value: property.valueDecimal,
          imageUrl: property.file,
        };
      }
      case 4: {
        return {
          value: property.valueText,
          imageUrl: property.file,
        };
      }
      case 5: {
        return property.valueChoices.map((x) => ({
          title: x.value,
          imageUrl: x.imageUrl,
        }));
      }
      case 6: {
        return {
          value: property.valueHTMLContent,
          imageUrl: property.file,
        };
      }
      default:
        return null;
    }
  }

  simpleFilters = computed(() => {
    return this.getFiltersFromNavigationDetail(this.item()).filter((x) => x.dataTypeId !== 5);
  });

  enumerableFilters = computed(() => {
    return this.getFiltersFromNavigationDetail(this.item()).filter((x) => x.dataTypeId === 5 && x.value?.length > 0);
  });

  mapItemName = computed(() => {
    if (localized(this.item().displayNameForApp, this.translocoService)) {
      return localized(this.item().displayNameForApp, this.translocoService);
    }
    return localized(this.item().name, this.translocoService);
  });

  availabilityText = computed(() => {
    const availability = this.availability();
    return this.availabilityTextMap[availability];
  });

  availabilityColor = computed(() => {
    const item = this.item();
    let color = '';
    switch (item.schedule.availabilityView[0]) {
      case '0':
        color = this.navigationSettings.design.background.reservableResource.free;
        break;
      case '1':
        color = this.navigationSettings.design.background.reservableResource.tentative;
        break;
      case '2':
        color = this.navigationSettings.design.background.reservableResource.reserved;
        break;
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
        color = this.navigationSettings.design.background.reservableResource.closed;
        break;
      case '8':
        color = this.navigationSettings.design.background.reservableResource.freeClosed;
        break;
      case '9':
        color = this.navigationSettings.design.background.reservableResource.reserved;
        break;
    }

    return { backgroundColor: this.removeAlphaFromRgba(color), color: this.getTextColorBasedOnBg(color) };
  });

  protected getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';

    const [r, g, b] = (color.match(/\d+/g) || []).map(Number);
    if (r === undefined || g === undefined || b === undefined) return '#212529';

    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 140 ? '#212529' : '#ffffff';
  }

  removeAlphaFromRgba(color: string): string {
    const rgbaPattern = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*[\d.]+\)$/;
    const match = color.match(rgbaPattern);

    // je to RGBA
    if (match) {
      const r = match[1];
      const g = match[2];
      const b = match[3];
      return `rgba(${r}, ${g}, ${b})`;
    } else {
      //neni RGBA vrat primo barvu
      return color;
    }
  }
}
